import React, { useEffect } from 'react';
import { HiOutlineAtSymbol, HiOutlineInbox, HiOutlinePaperClip } from 'react-icons/hi';
import { EmailEnvelope, TemplateVariable } from '../../../../../../types/communication';
import { isValidEmail } from '../../../../../../utils/EmailUtils';
import SectionCard from '../../shared/SectionCard';
import FixedRecipientField from '../../shared/FixedRecipientField';
import TokenListField from '../../shared/TokenListField';
import VariableTextField from '../../shared/VariableTextField';
import EmailAttachmentsEditor from './EmailAttachmentsEditor';
import { recipientToken } from '../recipientVariable';

interface EmailEnvelopeEditorProps {
    envelope: EmailEnvelope;
    variables: TemplateVariable[];
    disabled: boolean;
    onChange: (envelope: EmailEnvelope) => void;
}

const emailRule = (value: string) => (isValidEmail(value) ? null : `E-mail inválido: ${value}`);

const EMAIL_RECIPIENT_TOKEN = recipientToken('EMAIL');

const EmailEnvelopeEditor: React.FC<EmailEnvelopeEditorProps> = ({ envelope, variables, disabled, onChange }) => {
    const setEnvelope = (patch: Partial<EmailEnvelope>) => onChange({ ...envelope, ...patch });

    useEffect(() => {
        if (disabled) return;
        if (envelope.to.length === 0) {
            onChange({ ...envelope, to: [EMAIL_RECIPIENT_TOKEN] });
        }
    }, [disabled, envelope, onChange]);

    return (
        <div className="flex flex-col gap-4">
        <SectionCard title="Caixa de entrada" description="O que a pessoa vê antes de abrir o e-mail." icon={<HiOutlineInbox size={18} />}>
            <div className="grid grid-cols-1 gap-3">
                <VariableTextField
                    id="email-subject"
                    label="Assunto"
                    value={envelope.subject}
                    variables={variables}
                    disabled={disabled}
                    maxLength={150}
                    error={!disabled && !envelope.subject.trim() ? 'Obrigatório' : undefined}
                    onChange={(subject) => setEnvelope({ subject })}
                />
                <VariableTextField
                    id="email-preheader"
                    label="Texto de pré-visualização (opcional)"
                    value={envelope.preheader}
                    variables={variables}
                    disabled={disabled}
                    maxLength={120}
                    hint="Aparece ao lado do assunto em vários leitores de e-mail."
                    onChange={(preheader) => setEnvelope({ preheader })}
                />
            </div>
        </SectionCard>

        <SectionCard title="Remetente e destinatários" description="Use variáveis para endereços que mudam a cada envio." icon={<HiOutlineAtSymbol size={18} />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TokenListField
                    id="email-from"
                    label="Remetente"
                    values={envelope.from}
                    variables={variables}
                    max={1}
                    validate={emailRule}
                    disabled={disabled}
                    placeholder="cobranca@agrolend.agr.br"
                    error={!disabled && !envelope.from.length ? 'Obrigatório' : undefined}
                    onChange={(from) => setEnvelope({ from })}
                />
                <VariableTextField
                    id="email-reply-to"
                    label="Respostas vão para"
                    value={envelope.replyTo}
                    variables={variables}
                    disabled={disabled}
                    onChange={(replyTo) => setEnvelope({ replyTo })}
                />
                <div className="md:col-span-2">
                    <FixedRecipientField
                        label="Para"
                        token={EMAIL_RECIPIENT_TOKEN}
                        hint="Preenchido automaticamente com o e-mail do destinatário de cada envio."
                    />
                </div>
                <TokenListField
                    id="email-cc"
                    label="Com cópia (Cc)"
                    values={envelope.cc}
                    variables={variables}
                    validate={emailRule}
                    disabled={disabled}
                    onChange={(cc) => setEnvelope({ cc })}
                />
                <TokenListField
                    id="email-bcc"
                    label="Cópia oculta (Cco)"
                    values={envelope.bcc}
                    variables={variables}
                    validate={emailRule}
                    disabled={disabled}
                    hint="Recebem sem que os outros vejam."
                    onChange={(bcc) => setEnvelope({ bcc })}
                />
            </div>
        </SectionCard>

        <SectionCard
            title="Anexos e downloads"
            description="Arquivo junto no e-mail ou link com validade para baixar."
            icon={<HiOutlinePaperClip size={18} />}
        >
            <EmailAttachmentsEditor
                attachments={envelope.attachments}
                variables={variables}
                disabled={disabled}
                onChange={(attachments) => setEnvelope({ attachments })}
            />
        </SectionCard>

        </div>
    );
};

export default EmailEnvelopeEditor;
